import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { ParticleBackground } from './ParticleBackground';
import { FloatingElements } from './FloatingElements';
import { AnimatedBlob } from './AnimatedBlob';

function XLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="1 2 22 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  return (
    <footer className="relative border-t py-16 bg-muted/20 overflow-hidden">
      {/* Background Animations */}
      <ParticleBackground />
      <FloatingElements />
      <AnimatedBlob />
      <div className="container mx-auto px-4 relative z-10">
        {/* Top: 4-column grid */}
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand column */}
          <div>
            <div className="flex items-center gap-3 mb-4 h-10">
              <img src="/logo.png" alt="SetOpenClaw" className="h-10 w-10 rounded-lg object-contain" />
              <span className="text-2xl font-bold">SetOpenClaw</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              {t('landing.footer.tagline')}
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/infoinlet"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-lg bg-muted/60 hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://x.com/infoinletcom"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-lg bg-muted/60 hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                aria-label="X (formerly Twitter)"
              >
                <XLogo className="h-[18px] w-[18px]" />
              </a>
            </div>
          </div>

          {/* Product column */}
          <div>
            <h4 className="font-semibold mb-4">{t('landing.footer.product')}</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><Link to="/#features" className="hover:text-foreground transition-colors">{t('landing.features.title')}</Link></li>
              <li><Link to="/#pricing" className="hover:text-foreground transition-colors">{t('landing.pricing.title')}</Link></li>
              <li><Link to="/docs" className="hover:text-foreground transition-colors">{t('nav.docs')}</Link></li>
            </ul>
          </div>

          {/* Company column */}
          <div>
            <h4 className="font-semibold mb-4">{t('landing.footer.company')}</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><Link to="/blog" className="hover:text-foreground transition-colors">{t('landing.footer.blog')}</Link></li>
              <li><Link to="/#faq" className="hover:text-foreground transition-colors">FAQ</Link></li>
              <li>
                <Link to="/#contact" className="hover:text-foreground transition-colors">
                  {t('landing.footer.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal column */}
          <div>
            <h4 className="font-semibold mb-4">{t('landing.footer.legal')}</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground transition-colors">{t('landing.footer.privacy')}</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors">{t('landing.footer.terms')}</Link></li>
              <li><Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link></li>
              <li><Link to="/data-deletion" className="hover:text-foreground transition-colors">Data Deletion</Link></li>
            </ul>
          </div>
        </div>

        {/* Newsletter — full-width section */}
        <div className="border-t mt-12 pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-md">
            <h3 className="text-2xl font-bold mb-2">Stay in the loop</h3>
            <p className="text-sm text-muted-foreground">
              Get the latest updates, product news, and announcements delivered to your inbox.
            </p>
          </div>
          <form
            className="flex gap-2 w-full md:max-w-md"
            onSubmit={(e) => e.preventDefault()}
          >
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
            <Button type="submit" size="lg" className="shrink-0 gap-2">
              {t('landing.footer.subscribe')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
        </div>

        {/* Bottom bar */}
        <div className="border-t mt-10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} {t('landing.footer.copyright')}</span>
          <div className="flex items-center gap-1">
            <span>{t('landing.footer.madeWith')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
